import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

/**
 * AdminInternalDocsDownload — single utility route that turns the
 * founder-only `/api/admin/internal-docs/{slug}` endpoint into a
 * clickable URL.
 *
 * It does NOT list anything, does NOT preview anything, and does
 * NOT expose the documentation inside the product. It just performs
 * one authenticated download using the admin's already-stored JWT
 * and then redirects back to /admin.
 *
 * Visit while signed in as admin:
 *   /admin/internal-docs/design-system.pdf
 *   /admin/internal-docs/design-system.md
 */
const FILENAMES = {
  'design-system.pdf': 'Maestra-Design-System.pdf',
  'design-system.md':  'Maestra-Design-System.md',
};

const AdminInternalDocsDownload = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('downloading'); // 'downloading' | 'done' | 'forbidden' | 'notfound' | 'error'

  useEffect(() => {
    if (!FILENAMES[slug]) { setState('notfound'); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/admin/internal-docs/${slug}`, { responseType: 'blob' });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = FILENAMES[slug];
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 800);
        setState('done');
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) setState('forbidden');
        else if (status === 404) setState('notfound');
        else setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Auto-return to /admin shortly after a successful download.
  useEffect(() => {
    if (state !== 'done') return;
    const t = setTimeout(() => navigate('/admin', { replace: true }), 1600);
    return () => clearTimeout(t);
  }, [state, navigate]);

  const COPY = {
    downloading: { eyebrow: 'Founder archive',  title: 'Preparing your download…' },
    done:        { eyebrow: 'Founder archive',  title: 'Your download has started.' },
    forbidden:   { eyebrow: 'Restricted',       title: 'Admin sign-in required.' },
    notfound:    { eyebrow: 'Not found',        title: 'That archive entry does not exist.' },
    error:       { eyebrow: 'Something is off', title: 'The download could not be completed.' },
  };
  const copy = COPY[state] || COPY.downloading;

  return (
    <div
      data-testid="admin-internal-docs-download"
      style={{
        minHeight: '100vh',
        background: 'var(--kahla-cream-light, #faf8f5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '32px',
      }}
    >
      <div style={{ maxWidth: 520, textAlign: 'center' }}>
        <p style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 11, letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'var(--kahla-text-light)',
          margin: '0 0 18px',
        }}>{copy.eyebrow}</p>
        <h1 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 32, fontWeight: 400,
          letterSpacing: '-0.005em',
          color: 'var(--kahla-charcoal, #2a2a2a)',
          lineHeight: 1.2,
          margin: '0 0 16px',
        }}>{copy.title}</h1>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic',
          fontSize: 16, lineHeight: 1.7,
          color: 'var(--kahla-text-light)',
          margin: 0,
        }}>
          {state === 'downloading' && 'Authenticating with your admin session and packaging the file. This usually takes a second.'}
          {state === 'done' && 'You will be returned to the admin panel in a moment.'}
          {state === 'forbidden' && 'Please sign in as an administrator and reopen this link.'}
          {state === 'notfound' && 'Check the URL — only design-system.pdf and design-system.md are published here.'}
          {state === 'error' && 'Try again in a moment, or fetch the file directly via curl using your admin token.'}
        </p>
        {(state === 'forbidden' || state === 'error') && (
          <button
            type="button"
            onClick={() => navigate('/admin', { replace: true })}
            data-testid="internal-docs-back-btn"
            style={{
              marginTop: 22,
              background: 'transparent',
              border: '1px solid var(--kahla-border)',
              padding: '10px 22px',
              fontFamily: 'inherit',
              fontSize: 11, letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              cursor: 'pointer',
            }}
          >Return to admin</button>
        )}
      </div>
    </div>
  );
};

export default AdminInternalDocsDownload;
